import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, Check } from 'lucide-react';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { uiLang } from '@/i18n/lang';

/**
 * Public product page for the Personal Relocation Report. Always visible
 * (unlike the in-funnel upsell card, which is flagged) so the product is
 * discoverable and reviewable — search engines, link previews, and the
 * payment provider's reviewers all land here. Pricing + what's-included +
 * all three policy links live on this single URL.
 */
export default function ReportLandingPage() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const lp = useLocalizedPath();

  useSeoMeta({
    title: t('reportLanding.metaTitle'),
    description: t('reportLanding.metaDescription'),
    locale: lang === 'ru' ? 'ru_RU' : 'en_US',
  });

  const bullets = [
    t('reportLanding.b1'),
    t('reportLanding.b2'),
    t('reportLanding.b3'),
    t('reportLanding.b4'),
  ];

  const steps = [
    { n: '1', title: t('reportLanding.h1step'), body: t('reportLanding.h1body') },
    { n: '2', title: t('reportLanding.h2step'), body: t('reportLanding.h2body') },
    { n: '3', title: t('reportLanding.h3step'), body: t('reportLanding.h3body') },
  ];

  const quizHref = '/quiz';

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 text-ink-700">
      {/* Hero */}
      <header className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-paper-2 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-terra-700">
          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
          {t('reportLanding.eyebrow')}
        </span>
        <h1 className="mt-4 font-display text-3xl italic text-ink-900 sm:text-4xl">
          {t('reportLanding.h1')}
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base text-ink-600">
          {t('reportLanding.sub')}
        </p>
        <p className="mt-4 font-mono text-sm tracking-wide text-terra-700">
          {t('reportLanding.price')}
        </p>
        <Link to={quizHref} className={cn(buttonVariants({ size: 'lg' }), 'mt-5')}>
          {t('reportLanding.cta')}
        </Link>
      </header>

      {/* What's inside */}
      <section className="mt-12 rounded-xl border border-ink-100 bg-paper-2 p-6">
        <h2 className="font-display text-xl italic text-ink-900">
          {t('reportLanding.includesTitle')}
        </h2>
        <ul className="mt-4 space-y-2.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2.5 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-terra-600" aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* How it works */}
      <section className="mt-10">
        <h2 className="font-display text-xl italic text-ink-900">
          {t('reportLanding.howTitle')}
        </h2>
        <ol className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {steps.map((s) => (
            <li key={s.n} className="rounded-lg border border-ink-100 bg-paper p-4">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-terra-600 font-mono text-xs text-white">
                {s.n}
              </span>
              <h3 className="mt-3 text-sm font-semibold text-ink-900">{s.title}</h3>
              <p className="mt-1 text-sm text-ink-600">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Premade / automated clarification */}
      <p className="mt-8 rounded-lg border border-ink-100 bg-paper-2 px-4 py-3 text-sm text-ink-600">
        {t('reportLanding.premade')}
      </p>

      {/* CTA + policies */}
      <div className="mt-10 text-center">
        <Link to={quizHref} className={cn(buttonVariants({ size: 'lg' }))}>
          {t('reportLanding.cta')}
        </Link>
        <p className="mt-4 font-mono text-[11px] text-ink-500">
          {t('reportLanding.policiesLead')}{' '}
          <Link to="/terms" className="underline hover:text-ink-900">
            {t('reportLanding.terms')}
          </Link>
          {' · '}
          <Link to={lp('/refund')} className="underline hover:text-ink-900">
            {t('reportLanding.refund')}
          </Link>
          {' · '}
          <Link to="/privacy" className="underline hover:text-ink-900">
            {t('reportLanding.privacy')}
          </Link>
        </p>
      </div>
    </div>
  );
}
